/**
 * Chunk Load Error Boundary
 *
 * A specific error boundary component that catches lazy loading errors.
 * It displays a user-friendly error message with a "Reload" button.
 *
 * This handles the case where:
 * 1. A new version is deployed
 * 2. The user tries to navigate to a page
 * 3. The browser tries to fetch an old chunk hash that no longer exists
 * 4. The lazyWithReload helper in routes.tsx tries to reload (up to 2 times)
 * 5. If reloads fail (e.g. persistent network issue or browser extension blocking),
 *    this component catches the final error and shows the UI.
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '@/shared/utils/logger';
import { useTelemetryStore } from '@/shared/store/telemetryStore';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ChunkLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error - use critical to ensure visibility in production
    logger.critical('ChunkLoadErrorBoundary caught an error:', error, errorInfo);
    // Buffer for the next heartbeat so chunk-load failures land in session history
    useTelemetryStore.getState().recordError(
      'chunk',
      error.message || 'Chunk load error',
      error.stack
    );
  }

  handleReload = () => {
    // Clear the reload attempt counter so we can try again fresh
    sessionStorage.removeItem('chunk_reload_attempts');
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isChunkError =
        this.state.error?.name === 'ChunkLoadError' ||
        this.state.error?.message?.includes('Failed to fetch dynamically imported module') ||
        this.state.error?.message?.includes('Importing a module script failed') ||
        this.state.error?.message?.includes('error loading dynamically imported module') ||
        this.state.error?.message?.includes('Unable to load application resources');

      if (isChunkError) {
        return (
          <div className="flex flex-col items-center justify-center min-h-screen bg-surface-inset p-4 text-center">
            <div className="bg-surface p-8 rounded-xl shadow-lg max-w-md w-full">
              <div className="w-16 h-16 bg-warning-soft text-warning-mid rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="fa-solid fa-triangle-exclamation text-3xl"></i>
              </div>

              <h2 className="text-xl font-bold text-ink mb-3">
                Update Required
              </h2>

              <p className="text-ink-body mb-6">
                A new version of the application is available, or a browser extension blocked the page from loading.
              </p>

              <button
                onClick={this.handleReload}
                className="w-full py-3 px-4 bg-action hover:bg-action-hover text-ink-inverse font-medium rounded-lg transition-colors flex items-center justify-center"
              >
                <i className="fa-solid fa-rotate-right mr-2"></i>
                Reload Page
              </button>

              <p className="text-xs text-ink-faint mt-6">
                If this persists, try disabling ad blockers or privacy extensions.
              </p>
            </div>
          </div>
        );
      }

      // For other errors, fall back to standard behavior (or re-throw)
      return this.props.children;
    }

    return this.props.children;
  }
}